import React from 'react';
import { X, AlertCircle, Info } from 'lucide-react';
import HearenField from '@/components/HearenField';
import useLanguage from '@/hooks/useLanguage';
import { quantizeDb } from '@/utils/tinnitusDisplay';

const AudioProfileModal = ({
  isOpen,
  onClose,
  audioService,
  isPlaying,
  equalizerSettings,
  equalizerEnabled,
  setEqualizerEnabled,
  notchEnabled,
  setNotchEnabled,
  labProgram,
  onPlayPause,
  t,
  profileConfigured = false,
  onApplySettings,
}) => {
  const { isFr } = useLanguage();

  if (!isOpen) return null;

  const hasAudioTest = !!equalizerSettings?.has_audiometric_compensation;
  const tinnitusFrequency =
    labProgram?.f_t ||
    equalizerSettings?.tinnitus_frequency ||
    equalizerSettings?.inverse_notch?.center_frequency ||
    null;

  const rawTinnitusGain =
    labProgram?.gain_db ||
    equalizerSettings?.inverse_notch?.gain ||
    null;

  const tinnitusGain =
    rawTinnitusGain !== null ? quantizeDb(rawTinnitusGain) : null;

  const hasTinnitusTest = tinnitusFrequency !== null && tinnitusFrequency > 0;
  const currentState = !hasAudioTest ? 1 : !hasTinnitusTest ? 2 : 3;

  const getProfileStatusText = () => {
    if (!equalizerEnabled) return null;
    if (currentState === 1) return t?.defaultProfile || 'Profil par défaut';
    return `${t?.active || 'Actif'} · ${t?.personalized || 'personnalisé'}`;
  };
  const activeFrequencies = (equalizerSettings?.audiometric_gains || [])
    .filter((band) => Math.abs(Number(band.gain || 0)) > 0)
    .map((band) => `${Math.round(Number(band.frequency))} Hz`);

  const handleEqualizerToggle = () => {
    const newValue = !equalizerEnabled;
    setEqualizerEnabled(newValue);

    if (onApplySettings) {
      onApplySettings({
        equalizerEnabled: newValue,
        notchEnabled,
      });
    }
  };

  const handleNotchToggle = () => {
    if (!hasTinnitusTest) return;

    const newValue = !notchEnabled;
    setNotchEnabled(newValue);

    if (onApplySettings) {
      onApplySettings({
        equalizerEnabled,
        notchEnabled: newValue,
      });
    }
  };

  return (
    <div className="fixed inset-0 z-[200] bg-[#0A0F1C] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div>
          <h2 className="text-white font-semibold text-lg">
            {t?.audioProfileLabel || 'Profil audio'}
          </h2>

          {equalizerEnabled && (
            <p className="text-purple-400 text-sm mt-0.5">
              {getProfileStatusText()}
            </p>
          )}
        </div>

        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 overflow-auto">
        {isPlaying ? (
          <>
            {/* Hearen Field */}
            <div className="flex flex-col items-center justify-center py-5">
              <HearenField
                isPlaying={isPlaying}
                eqEnabled={equalizerEnabled}
                notchEnabled={notchEnabled}
              />

              <p className="text-white/50 text-xs mt-4 text-center max-w-xs">
                {t?.audioProfileDescription ||
                  'Votre écoute est adaptée en temps réel pour plus de confort.'}
              </p>
            </div>

            {/* Toggles directly under animation */}
            <div className="mt-4 space-y-3">
              {/* Audio profile toggle */}
              <div className="p-4 bg-white/10 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white">
                      {t?.audioProfileLabel || 'Profil audio'}
                    </span>

                    {currentState === 1 ? (
                      <span className="ml-2 text-white/60 text-sm">
                        ({isFr ? 'défaut' : 'default'} · 7{' '}
                        {t?.frequencies || 'fréquences'})
                      </span>
                    ) : (
                      <span className="ml-2 text-white/60 text-sm">
                        (7 {t?.frequencies || 'fréquences'})
                      </span>
                    )}
                  </div>

                  <button
                    onClick={handleEqualizerToggle}
                    className={`px-4 py-2 rounded-full font-medium transition-colors ${
                      equalizerEnabled
                        ? 'bg-purple-600 text-white shadow-[0_0_12px_rgba(147,51,234,0.5)]'
                        : 'bg-white/20 text-white/80'
                    }`}
                  >
                    {equalizerEnabled ? 'ON' : 'OFF'}
                  </button>
                </div>

                {currentState === 1 && (
                  <div className="flex items-center gap-2 mt-2 text-amber-400/80 text-sm">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {isFr
                        ? 'Aucun test auditif effectué'
                        : 'No hearing test completed'}
                    </span>
                  </div>
                )}
              </div>

              {/* Tinnitus toggle */}
              <div className="p-4 bg-white/10 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white">
                      {t?.tinnitusLabel || 'Acouphènes'}
                    </span>

                    {currentState === 3 ? (
                      <span className="ml-2 text-white/60 text-sm">
                        ({Math.round(tinnitusFrequency)} Hz)
                      </span>
                    ) : currentState === 2 ? (
                      <span className="ml-2 text-white/60 text-sm">
                        ({isFr ? 'non configuré' : 'not configured'})
                      </span>
                    ) : (
                      <span className="ml-2 text-white/60 text-sm">
                        ({isFr ? 'inactif' : 'inactive'})
                      </span>
                    )}
                  </div>

                  <button
                    onClick={handleNotchToggle}
                    disabled={!hasTinnitusTest}
                    className={`px-4 py-2 rounded-full font-medium transition-colors ${
                      !hasTinnitusTest
                        ? 'bg-white/10 text-white/40 cursor-not-allowed'
                        : notchEnabled
                          ? 'bg-purple-600 text-white shadow-[0_0_12px_rgba(147,51,234,0.5)]'
                          : 'bg-white/20 text-white/80'
                    }`}
                  >
                    {notchEnabled && hasTinnitusTest ? 'ON' : 'OFF'}
                  </button>
                </div>

                {currentState === 1 && (
                  <div className="flex items-center gap-2 mt-2 text-amber-400/80 text-sm">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {isFr
                        ? 'Aucun test acouphène effectué'
                        : 'No tinnitus test completed'}
                    </span>
                  </div>
                )}

                {currentState === 2 && (
                  <div className="flex items-center gap-2 mt-2 text-amber-400/80 text-sm">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {isFr
                        ? 'Aucune fréquence acouphène définie'
                        : 'No tinnitus frequency defined'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-48 bg-white/5 rounded-xl border border-white/10">
            <AlertCircle className="w-12 h-12 text-white/30 mb-3" />
            <p className="text-white/50 text-center">
              {t?.startPlayback || 'Lancez la lecture'}
            </p>
          </div>
        )}

{/* Applied audio corrections */}
{activeFrequencies.length > 0 && (
  <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4">
    <div className="flex items-center justify-between mb-3">
      <div>
        <div className="text-white text-sm font-medium">
          {isFr ? 'Personnalisé pour votre profil auditif' : 'Personalized for your hearing profile'}
        </div>
        <div className="text-white/40 text-xs mt-1">
          {isFr ? 'Fréquences optimisées' : 'Optimized frequencies'}
        </div>
      </div>

      <div className={`text-xs ${equalizerEnabled ? 'text-emerald-300' : 'text-white/35'}`}>
        {equalizerEnabled ? 'ON' : 'OFF'}
      </div>
    </div>

    <div className="text-white/75 text-sm leading-relaxed">
      {activeFrequencies.join(' · ')}
    </div>
  </div>
)}

        {/* Tinnitus correction */}
        {hasTinnitusTest && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-white text-sm font-medium">
                  {isFr ? 'Profil acouphènes' : 'Tinnitus profile'}
                </div>
                <div className="text-white/40 text-xs mt-1">
                  {isFr ? `Optimisé pour ${Math.round(tinnitusFrequency)} Hz` : `Optimized for ${Math.round(tinnitusFrequency)} Hz`}
                </div>
              </div>

              <div className="text-right">
                <div
                  className={`text-xs ${
                    notchEnabled ? 'text-emerald-300' : 'text-white/35'
                  }`}
                >
                  {notchEnabled ? 'ON' : 'OFF'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioProfileModal;